






















import { Component, Prop, Vue } from 'vue-property-decorator';
import { ConfigService } from '@/services/ConfigService';
import FeaturedProduct from './FeaturedProduct.vue';
import { LabelSelectionApi, OrderTemplateDto, OrderTemplatesApi, ProductDto } from '@eurogast/hub-client-typescript';
import { ApiService } from '@/services/ApiService';
import { DependencyService } from '@/services/DependencyService';

const PRODUCTS_PER_PAGE = 4;

@Component({components: {FeaturedProduct}})
export default class FeaturedProducts extends Vue {
  @Prop({required: true})
  public localSkus: string;

  public isLoggedIn: boolean = ConfigService.isLoggedIn();
  public currentPage: number = 0;
  public loaded: boolean = false;
  public products: ProductDto[] = [];
  public orderTemplates: OrderTemplateDto[] = [];
  public labelSelection: string[] = [];

  private productService = DependencyService.getProductService();
  private orderTemplatesApi = ApiService.wrap(OrderTemplatesApi);
  private labelSelectionApi = ApiService.wrap(LabelSelectionApi);

  public get skus() {
    let source = this.localSkus;
    if (source.startsWith('[')) {
      source = source.slice(1, -1);
    }
    return source.split(', ');
  }

  public get hasContent() {
    return this.loaded ? (this.products && this.products.length) : (this.skus && this.skus.length);
  }

  public get pageCount() {
    return Math.ceil(this.skus.length / PRODUCTS_PER_PAGE);
  }

  public mounted() {
    this.updatePolyfills();
    this.fetchProductDtos();

    if (ConfigService.hasAccessToken()) {
      this.fetchOrderTemplates();
      this.fetchSkusInLabelSelection();
    }
  }

  private async fetchProductDtos() {
    try {
      this.products = await this.productService.getProductBySkuBatch(this.skus, false);
      this.updatePolyfills();
      this.loaded = true;
    } catch (exception) {
      await ApiService.defaultErrorHandler(exception);
    }
  }

  private async fetchOrderTemplates() {
    try {
      const templateDtos = await this.orderTemplatesApi.getOwnOrderTemplates(
          ConfigService.getTenant(),
          ConfigService.currentCustomerId
      );
      this.orderTemplates = templateDtos
          .sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()));
    } catch (exception) {
      await ApiService.defaultErrorHandler(exception);
    }
  }

  private async fetchSkusInLabelSelection() {
    this.labelSelectionApi.getOwnLabelSkus(ConfigService.getTenant(), ConfigService.currentCustomerId)
        .then((labels) => this.labelSelection = labels)
        .catch(ApiService.defaultErrorHandler);
  }

  public inLabelSelection(sku: string): boolean {
    return this.labelSelection.indexOf(sku) !== -1;
  }

  public setPage(index: number) {
    this.currentPage = index;
    this.updatePolyfills();
  }

  public computeClasses(productIndex: number): string {
    if (this.isInCurrentPage(productIndex)) {
      return 'cms-fp-active';
    } else {
      return 'cms-fp-inactive';
    }
  }

  public isInCurrentPage(index: number): boolean {
    const firstCurrentProduct = this.currentPage * PRODUCTS_PER_PAGE;
    return index >= firstCurrentProduct && index < (firstCurrentProduct + PRODUCTS_PER_PAGE);
  }

  public goToPreviousPage() {
    if (this.currentPage === 0) {
      this.currentPage = this.pageCount - 1;
    } else {
      this.currentPage--;
    }
    this.updatePolyfills();
  }

  public updatePolyfills() {
    this.$nextTick(() => {
      const isIE11 = window.navigator.userAgent.match(/Trident.*rv:11\./);
      if (isIE11) {
        /*
        Force a height redraw in IE 11; For some reason, it doesn't recalculate the flexbox height properly
        when the element is dynamically added, and adding this the the styles does nothing. It only has the desired
        effect (parent expands to fit the biggest product box and all others have the same height) if applied
        dynamically. Hovering over the element also fixes it so there's that.
         */
        Array.from((this.$refs.productsDiv as HTMLElement).children).forEach((child) => {
          (child as HTMLElement).focus();
          (child as HTMLElement).blur();
        });
      }
    });
  }

  public goToNextPage() {
    if (this.currentPage === (this.pageCount - 1)) {
      this.currentPage = 0;
    } else {
      this.currentPage++;
    }
    this.updatePolyfills();
  }
}
